import ClearIcon from '@mui/icons-material/Clear';
import ListViewIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/ViewModule';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import Tooltip from '@mui/material/Tooltip';
import { ReactComponent as AlertInfo } from 'assets/alertInfo.svg';
import downloadIcon from 'assets/exportPDF.png';
import reloadIcon from 'assets/reload.png';
import targetIcon from 'assets/target.png';
import { ReactComponent as AlertIcon } from 'assets/wg_alerts.svg';
import Button from 'components/Button';
import Link3DView from 'components/HeaderWellGuardian/components/Link3DView';
import MenuLink from 'components/HeaderWellGuardian/components/MenuLink';
import { PATHS } from 'constants/paths';
import { getIsSuperUser } from 'pages/WellGuardian/WIR/utils';
import { WirCreateTabs, WirInterventionTabs } from 'pages/WellGuardian/WIR/wir.constants';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { generatePath, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAssetFromSite } from 'types/wellGuardian';
import { useSelectUser } from '../../../../redux/User/selectors';
import {
  updateAllWellsSearchInput,
  updateAllWellsViewMode,
  updateDownload,
  updateInfoTab,
  updateInfoTips,
  updateReloadFetch,
  updateWellManagementModal,
  updateWIRCreateSelectedTab,
  updateWIRInterventionSelectedTab
} from '../../../../redux/WellGuardian';
import {
  useAllWellsSearchInput,
  useAllWellsViewMode,
  useCountryState,
  useIsSiteLayoutAvailable,
  useSelectAlertsCount,
  useSelectedAsset,
  useSelectedSite,
  useSiteActiveAlerts,
  useToggleSwitch,
  useWellManagementModal,
  useWIRCreateSelectedTab,
  useWIRInterventionSelectedTab,
  useWIRUsers
} from '../../../../redux/WellGuardian/selectors';

import SiteSelector from './../SiteSelector';
import styles from './WellGuardianToolbar.module.scss';
import WellManagement from './WellManagement/WellManagement';
import {
  getIsDerogations,
  getIsInterventionsWirCreation,
  getIsInterventionsWirList,
  getIsSynergiCases,
  getIsWellHealthAllWells,
  getIsWellHealthAllWellsAlerts,
  getIsWellHealthSubModule,
  ToolbarTab,
  WELL_HEALTH_TABS,
} from './utils/toolbarUtils';

export type ViewMode = 'grid' | 'list' | 'target';

const WellGuardianToolbar: React.FunctionComponent<{ path: string }> = ({ path }) => {
  const isWellHealthSubModule = getIsWellHealthSubModule(path);
  const isWellHealthAllWells = getIsWellHealthAllWells(path);
  const isWellHealthAllWellsAlerts = getIsWellHealthAllWellsAlerts(path);
  const isInterventionsWirList = getIsInterventionsWirList(path);
  const isInterventionsWirCreation = getIsInterventionsWirCreation(path);
  const isDerogations = getIsDerogations(path);
  const isSynergiCases = getIsSynergiCases(path);

  if (isWellHealthSubModule) return <WellGuardianTabsToolbar tabs={WELL_HEALTH_TABS} />;
  if (isWellHealthAllWells) return <WellGuardianSiteToolbar variant="all-wells" path={path} />;
  if (isWellHealthAllWellsAlerts) return <WellGuardianSiteToolbar variant="default" path={path} />;
  if (isInterventionsWirList)
    return <WellGuardianInterventionToolbar variant="wir-list-schedule" />;
  if (isInterventionsWirCreation) return <WellGuardianInterventionToolbar variant="wir-creation" />;
  if (isDerogations || isSynergiCases)
    return <WellGuardianSiteToolbar variant="site-selector-only" path={path} />;

  return null;
};

const WellGuardianTabsToolbar: React.FunctionComponent<{ tabs: ToolbarTab[] }> = ({ tabs }) => {
  const intl = useIntl();
  const { tuwi } = useParams<{ tuwi: string }>();

  const alertsCount = useSelectAlertsCount();

  const countrySelect = useCountryState();

  const fetchUserSite = useSelectedSite();

  const asset = getAssetFromSite(fetchUserSite, countrySelect);

  return (
    <div className={styles.menusBar}>
      <div className={styles.tabsContainer}>
        {tabs.map(menu => {
          const menuLabel = intl.formatMessage({ id: menu.label });
          const titleLabel = intl.formatMessage({ id: menu.title });

          return (
            <MenuLink
              key={menu.label}
              to={generatePath(menu.to, { tuwi })}
              icon={menu.icon}
              label={menuLabel}
              title={titleLabel}
            />
          );
        })}
      </div>
      <div className={styles.iconLinksContainer}>
        {/* r2SLink.url */}
        {(asset === 'NNS' || asset === 'CNS') && (
          <div className={styles.link3D}>
            {/* <Link3DView to={r2SLink.url} /> */}
            <Link3DView to="https://geospatial.cintoo.cloud/login" />
          </div>
        )}
        <MenuLink
          to={generatePath(PATHS.WELL_GUARDIAN_SINGLE_WELL_ALERTS, { tuwi })}
          icon={<AlertIcon />}
          count={alertsCount?.activeAlerts}
        />
      </div>
    </div>
  );
};

interface WellGuardianInterventionToolbarProps {
  variant: 'wir-list-schedule' | 'wir-creation';
}

const WellGuardianInterventionToolbar: React.FunctionComponent<
  WellGuardianInterventionToolbarProps
> = ({ variant }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const activeWirCreateTab = useWIRCreateSelectedTab();
  const activeWirInterventionTab = useWIRInterventionSelectedTab();

  return (
    <div className={styles.menusBar}>
      {variant === 'wir-creation' && (
        <>
          <div className={styles.tabsContainer}>
            <div
              data-testid="wir-creation-tab"
              className={
                activeWirCreateTab === WirCreateTabs.SUMMARY_LABEL
                  ? styles.activeTab
                  : styles.notActiveTab
              }
              onClick={() => dispatch(updateWIRCreateSelectedTab(WirCreateTabs.SUMMARY_LABEL))}
            >
              {intl.formatMessage({ id: 'wellGuardian.wir.wirCreation.summary' })}
            </div>
            <div
              data-testid="wir-creation-tab"
              className={
                activeWirCreateTab === WirCreateTabs.ACTIVITY_LABEL
                  ? styles.activeTab
                  : styles.notActiveTab
              }
              onClick={() => dispatch(updateWIRCreateSelectedTab(WirCreateTabs.ACTIVITY_LABEL))}
            >
              {intl.formatMessage({ id: 'wellGuardian.wir.wirCreation.activity' })}
            </div>
            <div
              data-testid="wir-creation-tab"
              className={
                activeWirCreateTab === WirCreateTabs.COMMENT_LABEL
                  ? styles.activeTab
                  : styles.notActiveTab
              }
              onClick={() => dispatch(updateWIRCreateSelectedTab(WirCreateTabs.COMMENT_LABEL))}
            >
              {intl.formatMessage({ id: 'wellGuardian.wir.wirCreation.comment' })}
            </div>
          </div>
          <div className={styles.rightTabs}>
            <div
              data-testid="wir-creation-tab"
              className={
                activeWirCreateTab === WirCreateTabs.DOWNLOAD_PDF
                  ? styles.activeTab
                  : styles.notActiveTab
              }
              onClick={() => {
                dispatch(updateWIRCreateSelectedTab(WirCreateTabs.DOWNLOAD_PDF));
                dispatch(updateDownload(true));
              }}
            >
              <Tooltip title="Download WIR as PDF">
                <div className={styles.download}>
                  <img src={downloadIcon} alt="" className={styles.downloadIcon} />
                </div>
              </Tooltip>
            </div>
            <div
              data-testid="wir-creation-tab"
              className={
                activeWirCreateTab === WirCreateTabs.INFO_LABEL
                  ? styles.activeTab
                  : styles.notActiveTab
              }
              onClick={() => {
                dispatch(updateWIRCreateSelectedTab(WirCreateTabs.INFO_LABEL));
                dispatch(updateInfoTab('ALL'));
                dispatch(updateInfoTips(null));
              }}
            >
              <AlertInfo />
            </div>
          </div>
        </>
      )}
      {variant === 'wir-list-schedule' && (
        <div className={styles.tabsContainer}>
          <div
            data-testid="wir-creation-tab"
            className={
              activeWirInterventionTab === WirInterventionTabs.LIST
                ? styles.activeTab
                : styles.notActiveTab
            }
            onClick={() => dispatch(updateWIRInterventionSelectedTab(WirInterventionTabs.LIST))}
          >
            {intl.formatMessage({ id: 'wellGuardian.wir.list' })}
          </div>
          <div
            data-testid="wir-creation-tab"
            className={
              activeWirInterventionTab === WirInterventionTabs.SCHEDULE
                ? styles.activeTab
                : styles.notActiveTab
            }
            onClick={() => dispatch(updateWIRInterventionSelectedTab(WirInterventionTabs.SCHEDULE))}
          >
            {intl.formatMessage({ id: 'wellGuardian.wir.schedule' })}
          </div>
          <div
            data-testid="wir-creation-tab"
            className={
              activeWirInterventionTab === WirInterventionTabs.STATISTICS
                ? styles.activeTab
                : styles.notActiveTab
            }
            onClick={() =>
              dispatch(updateWIRInterventionSelectedTab(WirInterventionTabs.STATISTICS))
            }
          >
            {intl.formatMessage({ id: 'wellGuardian.wir.statistics' })}
          </div>
        </div>
      )}
    </div>
  );
};

interface WellGuardianSiteToolbarProps {
  variant: 'default' | 'all-wells' | 'site-selector-only';
  path: string;
}

const WellGuardianSiteToolbar: React.FunctionComponent<WellGuardianSiteToolbarProps> = ({
  variant,
  path,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const isSynergiCases = getIsSynergiCases(path);

  const isSiteLayoutAvailable = useIsSiteLayoutAvailable();
  const viewMode = useAllWellsViewMode();
  const searchInput = useAllWellsSearchInput();
  const activeAlerts = useSiteActiveAlerts();
  const countrySelect = useCountryState();
  const fetchUserSite = useSelectedSite();
  const selectedAsset = useSelectedAsset() ?? 'N/A';

  const isSwitchOn = useToggleSwitch();

  const asset = getAssetFromSite(fetchUserSite, countrySelect);
  const user = useSelectUser();
  const users = useWIRUsers();

  const isSuperUser: any = getIsSuperUser(users, user, countrySelect);

  const modalOpen = useWellManagementModal();

  const handleUpdateViewMode = (viewMode: ViewMode) => {
    dispatch(updateAllWellsViewMode(viewMode));
  };

  useEffect(() => {
    if (!isSiteLayoutAvailable) {
      dispatch(updateAllWellsViewMode('list'));

      return;
    }
  }, [dispatch, isSiteLayoutAvailable]);

  const onSearchInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    dispatch(updateAllWellsSearchInput(e.target.value as string));
  };

  const onClearSearch = () => {
    dispatch(updateAllWellsSearchInput(''));
  };

  const handleFetchNewWells = async () => {
    dispatch(updateReloadFetch(true));
  };

  // const handleClick = () => {
  //   dispatch(updateToggleSwitch(!isSwitchOn));
  // };

  // useEffect(() => {
  //   dispatch(updateToggleSwitch(false));
  // }, [dispatch]);

  return (
    <>
      <div className={styles.menusBar}>
        <div className={styles.siteSelectorContainer}>
          <SiteSelector variant="dark" />
          {isSynergiCases && selectedAsset !== 'ALL' && fetchUserSite !== 'ALL' && (
            <span className={styles.synergiNote}>
              <AlertInfo className={styles.alert} />
              Additional Synergi Cases may be available besides the ones published below.
            </span>
          )}
        </div>
        <div className={styles.iconLinksContainer}>
          {/* Commented out as per the latest requirements */}
          {/* {variant === 'all-wells' && (
            <div className={styles.switchToggle}>
              <span className={styles.text}>Integrity</span>
              <Switch
                checked={isSwitchOn}
                onChange={handleClick}
                name="checklistDisabledStatus"
                sx={{
                  ml: 0,
                  '& .MuiSwitch-switchBase': {
                    color: styles.uncheckedColor,
                    '&.Mui-checked': {
                      color: '#00ebbf',
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#00ebbf',
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: '#00ebbf',
                  },
                }}
              />
              <span className={styles.text}>Opportunities</span>
            </div>
          )} */}
          {isSuperUser && variant === 'all-wells' && (
            <>
              <Button
                className={styles.linkButton}
                onClick={() => {
                  if (asset === 'ALL' && fetchUserSite === 'ALL') {
                    <>
                      {toast.error(
                        intl.formatMessage({
                          id: 'wellGuardian.allWells.AllWirAdditionError',
                        }),
                        {
                          hideProgressBar: false,
                          theme: 'colored',
                        },
                      )}
                    </>;
                  } else {
                    dispatch(updateWellManagementModal('add'));
                  }
                }}
              >
                <span className={styles.linkButtonText}>
                  {intl.formatMessage({ id: 'wellGuardian.allWells.AddNewWell' })}
                </span>
              </Button>
              {!isSwitchOn &&
                <div className={styles.reloadButton} onClick={handleFetchNewWells}>
                  <Tooltip
                    title="Click to fetch the newly added Wells"
                    classes={{ tooltip: styles.tooltips }}
                  >
                    <img src={reloadIcon} alt="" />
                  </Tooltip>
                </div>
              }
            </>
          )}
          {variant === 'all-wells' && (
            <>
              <Grow in={true}>
                <InputBase
                  id="search-well"
                  className={styles.searchInput}
                  autoComplete="off"
                  value={searchInput}
                  onChange={onSearchInputChange}
                  placeholder={intl.formatMessage({
                    id: 'wellGuardian.allWells.searchPlaceholder',
                  })}
                  startAdornment={
                    <InputAdornment position="start" className={styles.clearButton}>
                      <SearchIcon color="inherit" />
                    </InputAdornment>
                  }
                  endAdornment={
                    <IconButton size="small" className={styles.clearButton} onClick={onClearSearch}>
                      <ClearIcon fontSize="small" color="inherit" />
                    </IconButton>
                  }
                />
              </Grow>
              <div className={styles.viewModeButtonsContainer}>
                <Tooltip
                  title="Target Potential View - Producers Vs  Injector Wells"
                  classes={{ tooltip: styles.tooltips, arrow: styles.tooltipArrow }}
                  arrow
                >
                  <IconButton
                    data-testid="target-view"
                    size="small"
                    color="inherit"
                    onClick={() => handleUpdateViewMode('target')}
                  >
                    <img
                      src={targetIcon}
                      alt=""
                      className={viewMode === 'target' ? styles.selectedViewIcon : styles.viewIcon}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title="Grid View of Wells"
                  classes={{ tooltip: styles.tooltips, arrow: styles.tooltipArrow }}
                  arrow
                >
                  <IconButton
                    data-testid="grid-view"
                    size="small"
                    color="inherit"
                    onClick={() => handleUpdateViewMode('grid')}
                    disabled={!isSiteLayoutAvailable}
                    classes={{ root: styles.disabledButton }}
                  >
                    <GridViewIcon
                      className={viewMode === 'grid' ? styles.selectedViewIcon : styles.viewIcon}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title="List View of Wells"
                  classes={{ tooltip: styles.tooltips, arrow: styles.tooltipArrow }}
                  arrow
                >
                  <IconButton
                    data-testid="list-view"
                    size="small"
                    color="inherit"
                    onClick={() => handleUpdateViewMode('list')}
                  >
                    <ListViewIcon
                      className={viewMode === 'list' ? styles.selectedViewIcon : styles.viewIcon}
                    />
                  </IconButton>
                </Tooltip>
              </div>
            </>
          )}
          {variant !== 'site-selector-only' && (
            <>
              {/* r2SLink.url */}
              {(asset === 'NNS' || asset === 'CNS') && (
                <Tooltip
                  title="Well Platform  / Facilities  View"
                  classes={{ tooltip: styles.tooltips }}
                >
                  <div className={styles.link3D}>
                    {/* <Link3DView to={r2SLink.url} /> */}
                    <Link3DView to="https://geospatial.cintoo.cloud/login" />
                  </div>
                </Tooltip>
              )}
              <MenuLink
                to={generatePath(PATHS.WELL_GUARDIAN_MULTIPLE_WELLS_ALERTS)}
                icon={
                  <Tooltip title="Alerts Log" classes={{ tooltip: styles.tooltips }}>
                    <AlertIcon />
                  </Tooltip>
                }
                count={activeAlerts.length}
              />
            </>
          )}
        </div>
      </div>
      {modalOpen === 'add' && <WellManagement />}
    </>
  );
};

export default WellGuardianToolbar;
